// The admin panel's dropdown, generalised. Visually identical to the "Sort by"
// control on the Users and Authorities tabs (same pill trigger, blue label,
// chevron, and popover) — the analytics filters have to feel like the rest of
// the panel, not like raw <select> elements.
import React, { useEffect, useRef, useState } from 'react';
import { ChevronDown } from 'lucide-react';

export interface DropdownOption {
	value: string;
	label: string;
}

const Dropdown = ({
	value,
	options,
	onChange,
	width,
	ariaLabel,
}: {
	value: string;
	options: DropdownOption[];
	onChange: (value: string) => void;
	/** Optional explicit min width or width in px. */
	width?: number;
	ariaLabel?: string;
}) => {
	const [isOpen, setIsOpen] = useState(false);
	const ref = useRef<HTMLDivElement>(null);

	useEffect(() => {
		const handleClickOutside = (e: MouseEvent) => {
			if (ref.current && !ref.current.contains(e.target as Node)) {
				setIsOpen(false);
			}
		};
		if (isOpen) {
			document.addEventListener('mousedown', handleClickOutside);
		}
		return () => document.removeEventListener('mousedown', handleClickOutside);
	}, [isOpen]);

	const selected = options.find((o) => o.value === value) ?? options[0];

	return (
		<div className='relative inline-block text-left' ref={ref}>
			<button
				type='button'
				aria-label={ariaLabel}
				onClick={() => setIsOpen(!isOpen)}
				className='flex items-center justify-between gap-2 bg-gray-50 border border-gray-200 rounded-xl pl-3.5 pr-[9px] h-[34px] hover:bg-blue-50/50 transition-colors focus:ring-0 focus:outline-none outline-none group'
				style={width ? { minWidth: width } : undefined}
			>
				<div className='grid grid-cols-1 items-center flex-1 min-w-0'>
					{/* Hidden option spans force the grid column width to match the largest option label */}
					{options.map((o) => (
						<span
							key={o.value}
							aria-hidden='true'
							className='col-start-1 row-start-1 text-sm font-bold leading-normal invisible select-none whitespace-nowrap'
						>
							{o.label}
						</span>
					))}
					{/* Visible selected label */}
					<span className='col-start-1 row-start-1 text-sm font-bold text-blue-700 leading-normal truncate text-center z-10'>
						{selected?.label}
					</span>
				</div>
				<ChevronDown className='w-4 h-4 text-blue-700 shrink-0' />
			</button>
			{isOpen && (
				<div className='absolute left-0 top-full mt-1 max-h-64 overflow-y-auto bg-white border border-gray-100 rounded-xl shadow-xl z-[100] animate-in fade-in zoom-in duration-100 min-w-full w-max max-w-xs sm:max-w-md'>
					{options.map((o) => (
						<div
							key={o.value}
							className={`px-4 py-2 text-sm font-semibold cursor-pointer hover:bg-blue-50 transition-colors text-center whitespace-nowrap ${
								o.value === value ? 'text-blue-700 bg-blue-50/50' : 'text-gray-700'
							}`}
							onClick={() => {
								onChange(o.value);
								setIsOpen(false);
							}}
						>
							{o.label}
						</div>
					))}
				</div>
			)}
		</div>
	);
};

export default Dropdown;
